import React from "react";
import {
  Badge,
  Box,
  Button,
  Icon,
  IconProps,
  Popover,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import * as tokens from "@amzn/awsui-design-tokens-core";
import {
  InvestigationInboxItem,
  Stage,
} from "../../lusca-search-api/generated-src";
import {
  InvestigationApiError,
  useInvestigationInbox,
  useMarkInboxRead,
} from "../../hooks/useInvestigations";
import { formatTimestamp, notificationLabel } from "./investigationUtils";

/** Entries shown in the pane; the inbox endpoint returns at most 50 per page anyway. */
const PANE_LIMIT = 30;

/** "16:59" today, "28 Sep 16:59" otherwise; the full timestamp is the hover title. */
function shortTimestamp(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const time = d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
  if (d.toDateString() === new Date().toDateString()) return time;
  return `${d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
  })} ${time}`;
}

/** The investigation title without its " on <queue>" suffix (the full title is the hover text). */
function shortTitle(title: string): string {
  return title.replace(/ on [\w.-]+$/, "");
}

/**
 * One short line under the headline. Redrive/deletion messages carry their result in brackets
 * ("… completed (12 of 12 transaction(s) redriven); check the CBEs …") — show just that result;
 * the next step is on the case page. Anything else (comments, transfers) is shown as written.
 */
function detailLine(item: InvestigationInboxItem): string {
  // Plural "(s)" goes first so it is never mistaken for the bracketed result
  // ("3 message(s) returned …" used to show just "s").
  const text = (item.text ?? "")
    .replace(/\(s\)/g, "s")
    .replace(/\s+att-[0-9a-z]+/gi, "")
    .replace(/\s+after (?:attempt|redrive)$/i, "");
  if (/REDRIVE|DELETION/.test(item.type ?? "")) {
    const result = text.match(/\(([^()]*(?:\([^()]*\)[^()]*)*)\)/);
    if (result) return result[1];
  }
  return text;
}

type Tone = "success" | "error" | "warning" | "info";

function toneOf(type?: string | null): Tone {
  const t = type ?? "";
  if (/FAILED|INCOMPLETE/.test(t)) return "error";
  if (/RETURN/.test(t)) return "warning";
  if (/COMPLETED|VERIFIED|EXECUTED/.test(t)) return "success";
  return "info";
}

const TONE_ICON: Record<Tone, IconProps["name"]> = {
  success: "status-positive",
  error: "status-negative",
  warning: "status-warning",
  info: "status-info",
};
const TONE_VARIANT: Record<Tone, IconProps["variant"]> = {
  success: "success",
  error: "error",
  warning: "warning",
  info: "subtle",
};

const oneLine: React.CSSProperties = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

interface InboxCardProps {
  item: InvestigationInboxItem;
  onOpen: (item: InvestigationInboxItem) => void;
  onMarkRead: (item: InvestigationInboxItem) => void;
  marking: boolean;
}

/** A notification as a small clickable card: status icon, headline, case, one-line result. */
const InboxCard: React.FC<InboxCardProps> = ({
  item,
  onOpen,
  onMarkRead,
  marking,
}) => {
  const [hover, setHover] = React.useState(false);
  const unread = !item.readAt;
  const title = item.title || item.investigationId;
  const tone = toneOf(item.type);
  const label = notificationLabel(item.type);
  const detail = detailLine(item);
  const when = shortTimestamp(item.createdAt);
  return (
    <li
      data-testid={`inbox-item-${item.id}`}
      data-unread={unread ? "true" : "false"}
      style={{ borderTop: `1px solid ${tokens.colorBorderDividerDefault}` }}
    >
      <div
        role="link"
        tabIndex={0}
        aria-label={`${label}: ${title}`}
        onClick={() => onOpen(item)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen(item);
          }
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          display: "flex",
          gap: 10,
          padding: "10px 8px",
          cursor: "pointer",
          // Cloudscape CSS variables are hashed, so use the token values, not raw var() names.
          background: hover
            ? tokens.colorBackgroundDropdownItemHover
            : "transparent",
        }}
      >
        <span style={{ paddingTop: 1 }}>
          <Icon name={TONE_ICON[tone]} variant={TONE_VARIANT[tone]} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ ...oneLine, fontWeight: unread ? 700 : 400 }}>
            {label}
          </div>
          <div style={oneLine} title={title}>
            <Box variant="span" fontSize="body-s">
              {shortTitle(title)}
            </Box>
          </div>
          <div
            style={oneLine}
            title={`${detail} · ${formatTimestamp(item.createdAt)}`}
          >
            <Box variant="span" color="text-body-secondary" fontSize="body-s">
              {[detail, when].filter(Boolean).join(" · ")}
            </Box>
          </div>
        </div>
        {unread && (
          // Unread dot; clicking it marks the entry read without opening the case.
          <button
            type="button"
            title="Mark as read"
            aria-label={`Mark "${label}" on ${title} as read`}
            disabled={marking}
            onClick={(e) => {
              e.stopPropagation();
              onMarkRead(item);
            }}
            onKeyDown={(e) => e.stopPropagation()}
            style={{
              alignSelf: "center",
              width: 20,
              height: 20,
              padding: 0,
              border: "none",
              background: "transparent",
              cursor: marking ? "default" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span
              aria-hidden
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: tokens.colorTextAccent,
                opacity: marking ? 0.4 : 1,
              }}
            />
          </button>
        )}
      </div>
    </li>
  );
};

interface InboxListProps {
  items: InvestigationInboxItem[];
  loading: boolean;
  onOpen: (item: InvestigationInboxItem) => void;
  onMarkRead: (item: InvestigationInboxItem) => void;
  marking: boolean;
  /** Extra action shown on the right of the footer (the bell passes "Mark all read"). */
  footerAction?: React.ReactNode;
}

/** The body of the bell popover: one card per notification, newest first, across every stage. */
export const InvestigationInboxList: React.FC<InboxListProps> = ({
  items,
  loading,
  onOpen,
  onMarkRead,
  marking,
  footerAction,
}) => {
  // Read entries are hidden until asked for; the toggle says how many there are.
  const [showRead, setShowRead] = React.useState(false);
  const shown = items.slice(0, PANE_LIMIT);
  const readCount = shown.filter((i) => !!i.readAt).length;
  const visible = showRead ? shown : shown.filter((i) => !i.readAt);
  const toggle = readCount > 0 && (
    <Button variant="inline-link" onClick={() => setShowRead((v) => !v)}>
      {showRead ? "Hide read" : `Show ${readCount} read`}
    </Button>
  );
  const footer = (toggle || footerAction) && (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        paddingTop: 8,
      }}
    >
      <span>{toggle}</span>
      <span>{footerAction}</span>
    </div>
  );
  if (!visible.length) {
    return (
      <SpaceBetween size="xxs">
        <Box color="text-body-secondary" padding={{ vertical: "xs" }}>
          {loading ? "Checking your notifications…" : "You're all caught up."}
        </Box>
        {footer}
      </SpaceBetween>
    );
  }
  return (
    <div>
      <ul
        style={{
          margin: 0,
          padding: 0,
          listStyle: "none",
          borderBottom: `1px solid ${tokens.colorBorderDividerDefault}`,
          // Scroll inside the pane, and always leave room below the bell (navbar + header + footer),
          // so the popover never has to flip above the trigger and off the top of the page.
          maxHeight: "min(480px, calc(100vh - 260px))",
          overflowY: "auto",
        }}
      >
        {visible.map((item) => (
          <InboxCard
            key={item.id}
            item={item}
            onOpen={onOpen}
            onMarkRead={onMarkRead}
            marking={marking}
          />
        ))}
      </ul>
      {footer}
    </div>
  );
};

interface InvestigationNotificationBellProps {
  currentUser: string | null;
  /** Open an investigation (the item carries its own stage: the inbox spans all of them). */
  onOpen: (investigationId: string, stage: Stage) => void;
  onError?: (message: string) => void;
  /** Render the popover in a portal (needed inside the dashboard drawer, which clips overflow). */
  renderWithPortal?: boolean;
}

/**
 * Notification bell fed by the signed-in user's inbox (`GET /investigations/inbox`): every owner
 * notification across stages, newest first, with the exact unread count as the badge. The same
 * events also go out by email and Slack through the Amazon Notifications service; this pane is the
 * in-app copy. Opening an entry marks it read; "Mark read" / "Mark all read" do so in place.
 */
export const InvestigationNotificationBell: React.FC<
  InvestigationNotificationBellProps
> = ({ currentUser, onOpen, onError, renderWithPortal = true }) => {
  const inbox = useInvestigationInbox(!!currentUser);
  const markRead = useMarkInboxRead() ?? {
    mutateAsync: () => Promise.resolve({ updatedCount: 0, unreadCount: 0 }),
    isLoading: false,
  };
  // Fail closed: an unavailable inbox (or a test that mocks the hook away) shows an empty bell.
  const items = inbox?.data?.items ?? [];
  const unread = inbox?.data?.unreadCount ?? 0;

  // Cloudscape's Popover is uncontrolled and floats above the sticky navbar, so close it when the
  // page scrolls (remount = closed), like a native menu. Scrolling the list inside it is fine.
  const [popoverKey, setPopoverKey] = React.useState(0);
  const contentRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const onScroll = (e: Event) => {
      const content = contentRef.current;
      if (!content) return; // closed
      if (e.target instanceof Node && content.contains(e.target)) return;
      setPopoverKey((k) => k + 1);
    };
    document.addEventListener("scroll", onScroll, true);
    return () => document.removeEventListener("scroll", onScroll, true);
  }, []);

  if (!currentUser) return null;

  const report = (what: string) => (err: InvestigationApiError) =>
    onError?.(`Could not ${what}: ${err.message}`);

  const markItem = (item: InvestigationInboxItem) =>
    markRead
      .mutateAsync({ notificationIds: [item.id] })
      .catch(report("mark the notification as read"));
  const markAll = () =>
    markRead.mutateAsync({ all: true }).catch(report("mark all as read"));
  const open = (item: InvestigationInboxItem) => {
    if (!item.readAt) {
      markItem(item).catch(console.error);
    }
    onOpen(item.investigationId, item.stage ?? Stage.Gamma);
  };

  const label = unread
    ? `Notifications: ${unread} unread`
    : "Notifications: nothing new";

  return (
    <Popover
      key={popoverKey}
      triggerType="custom"
      size="large"
      fixedWidth
      position="bottom"
      renderWithPortal={renderWithPortal}
      dismissButton
      dismissAriaLabel="Close notifications"
      header={
        unread
          ? `${unread} unread notification${unread === 1 ? "" : "s"}`
          : "Notifications"
      }
      content={
        <div ref={contentRef}>
          <SpaceBetween size="s">
            <InvestigationInboxList
              items={items}
              loading={!!inbox?.isLoading}
              onOpen={open}
              onMarkRead={(item) => {
                markItem(item).catch(console.error);
              }}
              marking={markRead.isLoading}
              footerAction={
                unread > 0 && (
                  <Button
                    variant="inline-link"
                    onClick={() => {
                      markAll().catch(console.error);
                    }}
                    loading={markRead.isLoading}
                  >
                    Mark all read
                  </Button>
                )
              }
            />
            {inbox?.error && (
              <Box color="text-status-error" fontSize="body-s">
                Could not load your notifications: {inbox.error.message}
              </Box>
            )}
          </SpaceBetween>
        </div>
      }
    >
      <span style={{ position: "relative", display: "inline-block" }}>
        <Button
          iconName="notification"
          variant="icon"
          ariaLabel={label}
          data-testid="notification-bell"
        />
        {!!unread && (
          <span
            aria-hidden
            style={{
              position: "absolute",
              top: -4,
              right: -6,
              pointerEvents: "none",
            }}
          >
            <Badge color="severity-high">{unread}</Badge>
          </span>
        )}
      </span>
    </Popover>
  );
};

export default InvestigationNotificationBell;
